import { Box, Button, Divider, Tooltip, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UndoIcon from '@mui/icons-material/Undo';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';

const buttonSx = { fontSize: 13, height: 34, px: 1.75, textTransform: 'uppercase', fontWeight: 500, '& .MuiButton-startIcon': { mr: 0.75, '& svg': { fontSize: 17 } } };

/**
 * 하단 고정 작업 바. 왼쪽에 Delete와 라인·수량·카톤·중량 요약, 오른쪽에 저장하지 않은 내용 표시 / Discard changes / Save Draft / Confirm.
 * 오류가 있거나 라인이 없으면 확정이 막힌다. 임시저장은 Save Draft로만 한다. 검증 오류가 있어도 저장할 수 있고, 고친 내용이 없으면 막힌다.
 * 확정본 수정(editing)에서는 Save Draft가 없다 — 수정 초안을 서버에 두지 않는다. 수정을 그만두려면 창을 닫는다. confirmBlocked면 오류와 별개로 확정을 막는다.
 * Delete는 확정본 보기에서는 확정 문서 자체를 지운다. deleteDisabled(수정 중)면 막고, Read-only로 돌아가야 지울 수 있다고 알린다.
 * 확정본 보기(readOnly)에서는 오른쪽이 Download(원본 파일이 있을 때)와 Edit뿐이다. Edit을 눌러야 수정 모드가 된다.
 * 확정본 수정에서는 Confirm 왼쪽의 Read-only(onReadOnly)로 다시 보기로 돌아간다.
 */
export default function ConfirmBar({ errorCount, itemCount, totalQty, totalCartons, totalWeight, weightUom, saving, dirty = false, onDelete, deleteDisabled = false, canDiscardChanges, onDiscardChanges, canSaveDraft, onSaveDraft, onConfirm, editing = false, confirmBlocked = false, readOnly = false, onEdit, onReadOnly, onDownload, downloading = false }) {
    const disabled = errorCount > 0 || itemCount === 0 || saving || confirmBlocked;
    const parts = [`${itemCount} lines`, `${totalQty.toLocaleString()} QTY`];
    if (totalCartons > 0) parts.push(`${totalCartons.toLocaleString()} CTN`);
    if (totalWeight > 0) parts.push(`${totalWeight.toLocaleString(undefined, { maximumFractionDigits: 1 })} ${weightUom || 'KG'}`);
    return (
        <Box sx={{ px: 2.5, py: 1.25, bgcolor: 'white', borderTop: '1px solid #dfdfdf', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', flexShrink: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Tooltip title={deleteDisabled ? 'This packing list is being edited. Go back to Read-only to delete it.' : ''} arrow>
                    <span>
                        <Button size="small" color="error" startIcon={<DeleteOutlineIcon />} onClick={onDelete} disabled={saving || deleteDisabled} sx={buttonSx}>Delete</Button>
                    </span>
                </Tooltip>
                <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />
                <Typography sx={{ fontSize: 13, color: '#003a52' }}><b>Totals:</b> {parts.join(' · ')}</Typography>
                {errorCount > 0 && !readOnly && <Typography sx={{ fontSize: 12, color: 'error.main' }}>Resolve {errorCount} error(s) before confirming.</Typography>}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, ml: 'auto' }}>
                {readOnly ? (
                    <>
                        {onDownload && (
                            <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={onDownload} disabled={downloading} sx={{ ...buttonSx, color: '#003a52', borderColor: '#003a52' }}>
                                Download
                            </Button>
                        )}
                        <Button size="small" variant="contained" disableElevation startIcon={<EditOutlinedIcon />} onClick={onEdit} disabled={saving} sx={{ ...buttonSx, px: 2.5, bgcolor: 'primary.dark', '&:hover': { bgcolor: '#002a3c' } }}>
                            Edit
                        </Button>
                    </>
                ) : (
                    <>
                        {dirty && (
                            <Typography sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: 12, color: 'warning.main', mr: 0.5 }}>
                                <Box component="span" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: 'warning.main' }} />
                                Unsaved changes
                            </Typography>
                        )}
                        <Button size="small" startIcon={<UndoIcon />} onClick={onDiscardChanges} disabled={saving || !canDiscardChanges} sx={{ ...buttonSx, color: '#003a52' }}>Discard changes</Button>
                        {!editing && (
                            <Button size="small" variant="outlined" startIcon={<SaveOutlinedIcon />} onClick={onSaveDraft} disabled={saving || !canSaveDraft} sx={{ ...buttonSx, color: '#003a52', borderColor: '#003a52' }}>
                                Save Draft
                            </Button>
                        )}
                        {onReadOnly && (
                            <Button size="small" variant="outlined" startIcon={<VisibilityOutlinedIcon />} onClick={onReadOnly} disabled={saving} sx={{ ...buttonSx, color: '#003a52', borderColor: '#003a52' }}>
                                Read-only
                            </Button>
                        )}
                        <Button size="small" variant="contained" disableElevation startIcon={<CheckIcon />} onClick={onConfirm} disabled={disabled} sx={{ ...buttonSx, px: 2.5, bgcolor: 'primary.dark', '&:hover': { bgcolor: '#002a3c' } }}>
                            {saving ? 'Saving…' : 'Confirm'}
                        </Button>
                    </>
                )}
            </Box>
        </Box>
    );
}
